import type { AutoApprove, InstagramStatus, Sources as SourcesT } from "@brandwall/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, del, patch, post } from "../api";
import { CheckNow } from "../components/CheckNow";
import { InstagramAccounts } from "../components/InstagramAccounts";
import { LabelsCard } from "../components/LabelsCard";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

const roles = {
  ambassador: "Ambassador",
  staff: "Staff",
  shop: "Partner shop",
  partner: "Partner",
} as const;

export function Sources() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const canEdit = workspace.role === "owner";
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["sources", ws];
  const sources = useQuery({ queryKey: key, queryFn: () => api<SourcesT>(`/v1/w/${ws}/sources`) });
  const ig = useQuery({
    queryKey: ["instagram", ws],
    queryFn: () => api<InstagramStatus>(`/v1/w/${ws}/instagram`),
  });
  const [tag, setTag] = useState("");
  const [user, setUser] = useState("");
  const [until, setUntil] = useState("");
  const [role, setRole] = useState<keyof typeof roles>("ambassador");
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (fn: () => Promise<SourcesT & { result?: { summary: string } }>) => fn(),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      setError(null);
      if (data.result) toast(data.result.summary);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Something went wrong."),
  });
  const s = sources.data;
  if (!s) return <p className="muted">Loading…</p>;

  return (
    <div className="stack" style={{ gap: 16, maxWidth: 760 }}>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <InstagramAccounts canEdit={canEdit} />

      <section className="card stack">
        <div className="row">
          <div className="spacer">
            <h2>Posts that tag or mention you</h2>
            <p className="muted">
              Collect posts where people tag{" "}
              {ig.data?.username ? `@${ig.data.username}` : "your account"} in the photo or mention
              it in the caption.
            </p>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={s.tagsEnabled}
              disabled={!canEdit}
              aria-label="Collect posts that tag or mention you"
              onChange={(e) =>
                run.mutate(() => patch(`/v1/w/${ws}/sources`, { tagsEnabled: e.target.checked }))
              }
            />
            <span />
          </label>
        </div>
      </section>

      {ig.data?.connected && (
        <section className="card">
          <CheckNow />
        </section>
      )}

      <section className="card stack">
        <div>
          <h2>Hashtags</h2>
          <p className="muted">
            Find good posts from anyone who uses these hashtags, even if they don't tag you. They
            show up in Discover, where you choose which creators to ask.
          </p>
        </div>
        <div className="row wrap">
          {s.hashtags.map((t) => (
            <span key={t} className="pill">
              #{t}
              {canEdit && (
                <button
                  type="button"
                  className="btn link"
                  aria-label={`Remove #${t}`}
                  onClick={() => run.mutate(() => del(`/v1/w/${ws}/hashtags/${t}`))}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
        {canEdit && (
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate(() => post(`/v1/w/${ws}/hashtags`, { tag }));
              setTag("");
            }}
          >
            <input
              className="input"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="#yourbrand"
              aria-label="Add a hashtag"
            />
            <button type="submit" className="btn secondary" disabled={!tag.trim()}>
              Add hashtag
            </button>
          </form>
        )}
        <p className="small muted">
          {s.hashtagLimit > 30
            ? `Instagram lets each account look up 30 different hashtags a week, so with your accounts you can follow ${s.hashtagLimit}. You have ${s.hashtags.length}.`
            : `Instagram lets you look up ${s.hashtagLimit} different hashtags a week. You have ${s.hashtags.length}.`}
        </p>
      </section>

      <LabelsCard canEdit={workspace.role !== "viewer"} />

      <section className="card stack">
        <div>
          <h2>People you trust</h2>
          <p className="muted">
            Ambassadors, staff and partner shops. Brandwall collects their recent posts, and you
            don't need to ask them for permission.
          </p>
        </div>
        {s.trusted.map((t) => (
          <div key={t.id} className="row">
            <div className="av" aria-hidden="true">
              {t.username.slice(0, 2).toUpperCase()}
            </div>
            <div className="spacer">
              <b>@{t.username}</b> <span className="small muted">{roles[t.role]}</span>
              {t.trustedUntil && (
                <span className="small muted">
                  {" "}
                  · until{" "}
                  {new Date(`${t.trustedUntil}T00:00:00Z`).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    timeZone: "UTC",
                  })}
                </span>
              )}
              {t.lastError && (
                <div className="small" style={{ color: "var(--warn)" }}>
                  {t.lastError}
                </div>
              )}
            </div>
            {canEdit && (
              <button
                type="button"
                className="btn secondary sm"
                onClick={() => run.mutate(() => del(`/v1/w/${ws}/trusted/${t.username}`))}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <form
            className="row wrap"
            onSubmit={(e) => {
              e.preventDefault();
              run.mutate(() =>
                post(`/v1/w/${ws}/trusted`, {
                  username: user,
                  role,
                  ...(until ? { trustedUntil: until } : {}),
                }),
              );
              setUser("");
              setUntil("");
            }}
          >
            <input
              className="input"
              style={{ flex: "1 1 200px" }}
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="@annavik"
              aria-label="Instagram username"
            />
            <select
              className="input"
              style={{ width: "auto" }}
              value={role}
              onChange={(e) => setRole(e.target.value as keyof typeof roles)}
              aria-label="Role"
            >
              {Object.entries(roles).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="date"
              style={{ width: "auto" }}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              aria-label="Trusted until (optional)"
              title="Trusted until (optional): after it, their new posts no longer go up by themselves"
            />
            <button type="submit" className="btn secondary" disabled={!user.trim()}>
              Add person
            </button>
          </form>
        )}
      </section>

      <AutoApproveCard
        value={s.autoApprove}
        trustedCount={s.trusted.length}
        canEdit={canEdit}
        onChange={(autoApprove) => run.mutate(() => patch(`/v1/w/${ws}/sources`, { autoApprove }))}
      />
    </div>
  );
}

/** Which new posts go on the walls by themselves (D-056). Tagged and hashtag posts never do. */
function AutoApproveCard({
  value,
  trustedCount,
  canEdit,
  onChange,
}: {
  value: AutoApprove;
  trustedCount: number;
  canEdit: boolean;
  onChange: (v: Partial<AutoApprove>) => void;
}) {
  const [tag, setTag] = useState("");
  const add = () => {
    const t = tag.trim().replace(/^#/, "").toLowerCase();
    if (!t || value.hashtags.includes(t)) return setTag("");
    onChange({ hashtags: [...value.hashtags, t] });
    setTag("");
  };
  return (
    <section className="card stack">
      <div>
        <h2>Auto-approve</h2>
        <p className="muted">
          Choose which new posts go on your walls without you looking first. Tagged and hashtag
          posts always wait for you, because their creators need to say yes.
        </p>
      </div>
      <div className="row">
        <div className="spacer">
          <b>Your own posts</b>
          <div className="small muted">New posts on your Instagram account.</div>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            checked={value.own}
            disabled={!canEdit}
            aria-label="Auto-approve your own posts"
            onChange={(e) => onChange({ own: e.target.checked })}
          />
          <span />
        </label>
      </div>
      <div className="row">
        <div className="spacer">
          <b>Posts from people you trust</b>
          <div className="small muted">
            {trustedCount === 0
              ? "Add people you trust above first."
              : value.hashtags.length
                ? `Only their posts with ${value.hashtags.map((h) => `#${h}`).join(" or ")}. Their other posts wait for you.`
                : "All their new posts. Add a hashtag below to only take posts about you."}
          </div>
        </div>
        <label className="switch">
          <input
            type="checkbox"
            checked={value.trusted}
            disabled={!canEdit}
            aria-label="Auto-approve posts from people you trust"
            onChange={(e) => onChange({ trusted: e.target.checked })}
          />
          <span />
        </label>
      </div>
      {value.trusted && (
        <div className="stack" style={{ gap: 8 }}>
          <span className="small">Only posts with one of these hashtags (optional)</span>
          {value.hashtags.length > 0 && (
            <div className="chips">
              {value.hashtags.map((h) => (
                <span key={h} className="chip">
                  #{h}
                  {canEdit && (
                    <button
                      type="button"
                      aria-label={`Remove #${h}`}
                      onClick={() => onChange({ hashtags: value.hashtags.filter((x) => x !== h) })}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {canEdit && (
            <div className="row">
              <input
                className="input spacer"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="#yourbrand"
                aria-label="Hashtag for auto-approve"
              />
              <button type="button" className="btn secondary" disabled={!tag.trim()} onClick={add}>
                Add hashtag
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
